/** Inline selection summary shown in the grid toolbar row once rows are ticked.
 *
 *  Mirrors the dashboard's SelectAllBanner UX — it distinguishes a page/manual
 *  selection ("N selected", offering "Select all N matching") from an
 *  all-matching selection ("All N matching selected") — but renders *inline*
 *  within `GridControlsToolbar` (next to the Batch-actions button) rather than
 *  as a bar above the grid, so engaging a selection never shifts the grid down.
 *  Purely presentational — the grid owns the state.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';

export interface SelectAllBannerProps {
  /** How many rows the user is currently acting on. */
  selectedCount: number;
  /** Grid total for the current query (the "N matching"), or null when the grid
   *  defers its count and no total has been resolved yet. With null the banner
   *  drops the number rather than the capability: "Select all matching" still
   *  works, because the server re-derives the matching set from the same filters
   *  when the batch is dispatched (and reports the real `matched` count then). */
  totalCount: number | null;
  /** True once "Select all matching" is engaged. */
  allMatching: boolean;
  /** Engage all-matching mode. */
  onSelectAllMatching: () => void;
  /** Drop the whole selection. */
  onClear: () => void;
}

export function SelectAllBanner({
  selectedCount,
  totalCount,
  allMatching,
  onSelectAllMatching,
  onClear,
}: SelectAllBannerProps) {
  if (selectedCount <= 0) return null;

  const canSelectAll = !allMatching && (totalCount === null || totalCount > selectedCount);

  // Wraps rather than squeezes: on a narrow toolbar "Select all N" moves under the count
  // instead of overlapping the controls beside it.
  return (
    <Box data-select-all-banner sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
      {allMatching && (
        <CheckCircleIcon fontSize="small" color="primary" aria-hidden sx={{ mr: 0.25 }} />
      )}
      <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
        {allMatching
          ? totalCount === null
            ? 'All matching selected'
            : `All ${totalCount.toLocaleString()} selected`
          : `${selectedCount.toLocaleString()} selected`}
      </Typography>
      {canSelectAll && (
        <Button
          size="small"
          variant="text"
          onClick={onSelectAllMatching}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0 }}
        >
          {totalCount === null ? 'Select all matching' : `Select all ${totalCount.toLocaleString()}`}
        </Button>
      )}
      <Tooltip title="Clear selection">
        <IconButton size="small" onClick={onClear} aria-label="Clear selection">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
